// Meta home — the Meta specialist team's daily board.
//
// The same roster as Advertising Home, on ONE channel, with the panels a Meta
// specialist works from rather than the ones an AM does. It IMPORTS the shared
// component set (AlertsPanel, VerdictStrip, PerformancePulse, BookTable) rather
// than copying it: the alert rules have been corrected twice in the last
// fortnight, and a copied rule set means the third correction gets made once
// and missed twice.
//
// ⚠️ NO ChannelComparison. It compares meta against google, which is
// meaningless here — that slot is where the Meta panels go. Named because "copy
// the page, then delete things" is how it would otherwise arrive.
//
// ⚠️⚠️ TWO PANELS WERE REMOVED FROM THIS PAGE ON 2026-09-17 AND MUST NOT BE
// RESTORED AS THEY WERE — both were briefing errors, not build errors:
//
//  · THE AD-REVIEW WORKLIST read `review_status` at `level='ad'`, where the DDL
//    annotates it as a CAMPAIGN field. At ad grain it is null, which is exactly
//    the "500 ads, status unknown" Scott saw. A rebuild is at campaign grain
//    and ⚠️ NEEDS A PROBE FIRST — a column's existence is not a column's
//    population, and only a query against real rows settles it.
//  · THE CREATIVE-WASTE LIST flipped the `HAVING` without scoping the
//    objective. An ad in a traffic/engagement/awareness campaign has zero leads
//    BY DEFINITION, so "highest spend, zero leads" returns non-lead campaigns
//    first and the list is mostly noise. The rebuild is one predicate
//    (`objective = 'OUTCOME_LEADS'`, or META_FORM_LEAD_SQL from campaignClass).
//
// The best-creatives gallery below is the OPPOSITE ranking and is the AM
// board's own component, reused rather than reimplemented.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/layout/PageHeader';
import { SelectField } from '../../components/ui/SelectField';
import {
  accountFacetsFromBook,
  filterRowsToAccounts,
} from '../../lib/advertisingHome/bookFilter';
import { ALL_ALERT_TYPES } from '../../lib/advertisingHome/alertsView';
import type { AlertType } from '../../lib/advertisingHome/alerts';
import { alertNoteKey } from '../../lib/advertisingHome/acknowledgements';
import { normalizeDisplayCurrency } from '../../lib/reporting/currencyConversion';
import {
  PULSE_METRIC_OPTIONS,
  isPulseMetric,
} from '../../lib/advertisingHome/pulseSeries';
import type { ComparisonMode } from '../../types/advertisingHome';
import { DateRangePicker } from '../reporting/DateRangePicker';
import { getFxRates } from '../reporting/api';
import { PanelTitle } from '../reporting/PanelTitle';
import { COMPARISON_MODE_OPTIONS } from './homePrefs';
import type { AccountLinkKey } from '../../lib/accountLinks';
import { AlertsPanel } from './AlertsPanel';
import { AlertNotes } from './AlertNotes';
import { VerdictStrip, type HealthFilter } from './VerdictStrip';
import { PerformancePulse } from './PerformancePulse';
import { BookTable } from './BookTable';
import { WinsWorries } from './WinsWorries';
import { CreativesCard } from './CreativesCard';
import { LeadSplitPanel } from './LeadSplitPanel';
import { HomeFilterBar } from './HomeFilterBar';
import { SpecialistCountsNote } from './SpecialistCountsNote';
import { useSpecialistBoard } from './useSpecialistBoard';
import { getHomeCreatives } from './api';

// This board shows ONE account link beside every account name.
const META_LINK_KEYS: AccountLinkKey[] = ['fbAdAccountUrl'];

export function MetaHomePage(): React.JSX.Element {
  const board = useSpecialistBoard('meta');
  const [healthFilter, setHealthFilter] = useState<HealthFilter>(null);
  const [alertsCollapsed, setAlertsCollapsed] = useState(false);
  const [alertTypes, setAlertTypes] = useState<Set<AlertType>>(() => new Set(ALL_ALERT_TYPES));

  const fxQuery = useQuery({
    queryKey: ['reporting-fx-rates'],
    queryFn: getFxRates,
    staleTime: 30 * 60 * 1000,
  });
  const fxSnapshot = fxQuery.data?.rates ?? null;
  const display = normalizeDisplayCurrency('native', fxSnapshot);

  const rangeValid = Boolean(board.range.from && board.range.to);

  // §9f — the best-performing creatives, the SAME component and the SAME
  // endpoint the AM board uses. ⚠️ `'cheapest_cpl'` is passed explicitly —
  // the direction is required at the boundary, so a board cannot inherit an
  // intent it never stated. The query KEY carries it too, or the two directions
  // would share a cache entry and hand one board the other's rows.
  const creativesQuery = useQuery({
    queryKey: [
      'advertising-home-creatives',
      board.scope,
      board.range.from,
      board.range.to,
      'cheapest_cpl',
    ],
    queryFn: () => getHomeCreatives(board.scope, board.range, 'cheapest_cpl'),
    enabled: rangeValid,
    staleTime: 5 * 60 * 1000,
  });

  const visibleAlerts = useMemo(
    () =>
      healthFilter === null
        ? board.visible
        : board.visible.filter(
            (a) => board.evaluation.healthByAccount.get(a.accountId) === healthFilter,
          ),
    [board.visible, board.evaluation, healthFilter],
  );
  const acknowledgedAlerts = useMemo(
    () =>
      healthFilter === null
        ? board.acknowledged
        : board.acknowledged.filter(
            (e) => board.evaluation.healthByAccount.get(e.alert.accountId) === healthFilter,
          ),
    [board.acknowledged, board.evaluation, healthFilter],
  );

  const bookRows = useMemo(
    () =>
      healthFilter === null
        ? board.bookRows
        : board.bookRows.filter((r) => r.health === healthFilter),
    [board.bookRows, healthFilter],
  );

  // ⚠️ Narrowed to the board's own accounts, like every other section — the
  // endpoint is scoped to the roster, the filter row is not.
  const creativeRows = useMemo(
    () => filterRowsToAccounts(creativesQuery.data?.rows ?? [], board.accounts),
    [creativesQuery.data, board.accounts],
  );

  const overview = board.overviewQuery.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Meta home"
        description="Every Meta account on the roster — what is broken, what is burning, and who to hand it back to."
      />

      <div className="flex flex-wrap items-center gap-2">
        <DateRangePicker
          preset={board.preset}
          onPresetChange={board.setPreset}
          custom={board.custom}
          onCustomChange={board.setCustom}
        />
        <SelectField
          size="sm"
          aria-label="Comparison mode"
          value={board.comparisonMode}
          onValueChange={(v) => board.setComparisonMode(v as ComparisonMode)}
          options={COMPARISON_MODE_OPTIONS}
          className="min-w-[13rem]"
        />
        {/* ⚠️ ONE platform's freshness — the other is nulled by the channel
            narrowing, so this page never quotes a date for data it is not
            showing. */}
        <span className="ml-auto text-xs text-muted-foreground">
          Meta data to {board.asOf.meta ?? '—'}
        </span>
      </div>

      {overview && board.channelBook.length > 0 && (
        <HomeFilterBar
          facets={accountFacetsFromBook(board.channelBook)}
          selection={board.filterSel}
          onChange={board.setFilterSel}
          amOptions={board.amOptions}
        />
      )}

      {board.overviewQuery.isLoading && (
        <p className="text-sm text-muted-foreground">Loading the Meta roster…</p>
      )}
      {board.overviewQuery.isError && (
        <p className="text-sm text-destructive">
          Could not load the roster — {(board.overviewQuery.error as Error).message}
        </p>
      )}

      {overview && board.accounts.length === 0 && !board.overviewQuery.isLoading && (
        <div className="rounded-lg border border-border bg-card p-6 text-sm">
          <p className="font-medium">No accounts are running Meta right now.</p>
          <p className="mt-1 text-muted-foreground">
            This board lists accounts with Meta enabled. Accounts running Google only appear on
            the Google board instead.
          </p>
        </div>
      )}

      {board.accounts.length > 0 && (
        <>
          <VerdictStrip counts={board.counts} filter={healthFilter} onFilter={setHealthFilter} />
          <SpecialistCountsNote channelLabel="Meta" />

          <AlertsPanel
            showLinkIcons
            linkKeys={META_LINK_KEYS}
            alerts={visibleAlerts}
            acknowledged={acknowledgedAlerts}
            onAcknowledge={board.onAcknowledge}
            onUnacknowledge={board.onUnacknowledge}
            busy={board.busy}
            collapsed={alertsCollapsed}
            onToggleCollapsed={() => setAlertsCollapsed(!alertsCollapsed)}
            typeFilter={alertTypes}
            onTypeFilterChange={setAlertTypes}
            // Single-channel board — a platform toggle would offer one option.
            platformFilter="all"
            onPlatformFilterChange={() => {}}
            // §11a — who to hand it back to. Specialist boards only.
            managerFor={(a) => board.amNameOf(a.accountId)}
            // §11b — the badge and the thread read the SAME list: the panel
            // calls this ONCE per row and hands the result to both, so a count
            // that disagrees with the notes underneath it is not expressible.
            notesFor={(a) => board.notesByAlert.get(alertNoteKey(a)) ?? []}
            renderAlertExtra={(a, notes) => (
              <AlertNotes
                notes={notes}
                onAdd={(body) => board.onAddNote(a, body)}
                busy={board.noteBusy}
              />
            )}
          />

          <section className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <PanelTitle>Performance pulse</PanelTitle>
              {/* ⚠️ Metric only. The pulse CHANNEL is fixed by which board you
                  are on, so a channel selector here would be a control with one
                  usable option. Persisted under this board's OWN grid key. */}
              <span className="ml-auto">
                <SelectField
                  size="sm"
                  aria-label="Pulse metric"
                  value={board.pulseMetric}
                  onValueChange={(v) => isPulseMetric(v) && board.setPulseMetric(v)}
                  options={PULSE_METRIC_OPTIONS}
                  className="min-w-[6.5rem]"
                />
              </span>
            </div>
            <PerformancePulse
              cards={board.pulse}
              display={display}
              snapshot={fxSnapshot}
              metric={board.pulseMetric}
              channel="meta"
              nativeCurrency={board.bookCurrency}
              channels={[board.channel]}
            />
          </section>

          <section className="space-y-2">
            <PanelTitle className="block">
              The roster — {board.range.from} → {board.range.to}
            </PanelTitle>
            {board.bookQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading the range…</p>
            ) : (
              <BookTable
                rows={bookRows}
                alertsByAccount={board.alertsByAccount}
                sortKey={board.sortKey}
                sortDir={board.sortDir}
                onSort={board.onSort}
                showPacing={false}
                display={display}
                snapshot={fxSnapshot}
                channels={[board.channel]}
                linkKeys={META_LINK_KEYS}
                /* §11d — the four lead columns. Gated on the breakdown
                   having ARRIVED rather than on which board this is: columns
                   turned on without rows would print a lane of em-dashes, which
                   is the §9c defect again. */
                metaLeadDetail={board.metaLeadsByAccount.size > 0}
              />
            )}
          </section>

          {board.bookQuery.data && (
            <WinsWorries
              wins={board.winsWorries.wins}
              worries={board.winsWorries.worries}
              leadsBack={board.winsWorries.leadsBack}
              leadsGone={board.winsWorries.leadsGone}
              nameOf={board.nameOf}
              linkKeys={META_LINK_KEYS}
            />
          )}

          {/* §11e — two splits of the same leads, on two axes, each with
              its own residual. Above the gallery because "where did the leads
              come from" frames which creatives are worth looking at. */}
          {board.metaLeadsByAccount.size > 0 && (
            <LeadSplitPanel
              rows={[...board.metaLeadsByAccount.values()].flat()}
              display={display}
              snapshot={fxSnapshot}
            />
          )}

          {/* §9f — the winners, at 4:5. Reusing the AM board's component is
              what keeps every creative image on the signed proxy path: a raw
              `thumbnail_url` is blocked by ad blockers (an `/ads/` segment on a
              facebook.com URL matches standard filter lists) and that has
              drifted back three times. One renderer, one place it can drift. */}
          {creativesQuery.data && (
            <CreativesCard
              rows={creativeRows}
              noLeadRows={filterRowsToAccounts(creativesQuery.data.noLeadRows ?? [], board.accounts)}
              brandWindows={creativesQuery.data.brandWindows ?? []}
              brandOfAccount={(id) => board.accounts.find((a) => a.id === id)?.brandAccountId ?? null}
              display={display}
              snapshot={fxSnapshot}
              linkKeys={META_LINK_KEYS}
            />
          )}
        </>
      )}
    </div>
  );
}
